import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'
import { CONTEXT_TYPE_OPTIONS } from 'controllers/vue_components/organizations/it_components/itComponentOptions.js'

const CACHE_TTL = 5 * 60 * 1000

// GET requests go through axios.get(url, { headers }) — query params must be
// embedded in the URL itself, they aren't sent from a request body.
function buildQuery(state) {
  const parts = [
    `page[number]=${state.page.number}`, `page[limit]=${state.page.limit}`,
    `sort_by=${state.sortBy}`, `sort_order=${state.sortOrder}`,
  ]
  if (state.search) parts.push(`search=${encodeURIComponent(state.search)}`)
  if (state.status) parts.push(`status=${encodeURIComponent(state.status)}`)
  if (state.type) parts.push(`type[]=${encodeURIComponent(state.type)}`)
  if (state.hasPendingVersions) parts.push('has_pending_versions=true')
  state.contextTypes.forEach((ct) => parts.push(`context_type[]=${encodeURIComponent(ct)}`))
  return parts.join('&')
}

// Admin review queue for the global components catalog — every
// GlobalComponent, approved or pending, regardless of which org (if any)
// submitted it or which context (IT Component / Application) it belongs to.
// See app/controllers/api/v1/admin/global_components_controller.rb.
export const useAdminGlobalComponentsStore = defineStore('adminGlobalComponents', {
  state: () => ({
    records: [],
    counts: { total: 0, pending: 0, approved: 0, rejected: 0, org_specific: 0, pending_versions: 0 },
    page: { current_page: 1, total_pages: 1, total_records: 0, number: 1, limit: 20 },
    search: '',
    status: '', // '' (all) | 'pending' | 'approved' | 'rejected' | 'org_specific'
    type: '', // '' (all) | one of the 25 type enum values
    // Toggled on by the header's "Pending Versions" button — shows only
    // components with at least one version still awaiting review. Versions
    // themselves are only ever moderated from within a component's own Edit
    // slide-over, not from this index, so this is purely a way to find them.
    hasPendingVersions: false,
    contextTypes: CONTEXT_TYPE_OPTIONS.map((o) => o.value), // both selected by default
    sortBy: 'created_at',
    sortOrder: 'DESC',
    fetchedAt: null,
    loading: false,
    error: null,
  }),

  getters: {
    isCacheValid: (state) =>
      state.fetchedAt !== null && Date.now() - state.fetchedAt < CACHE_TTL,
  },

  actions: {
    async fetchIfStale() {
      if (this.isCacheValid && this.records.length > 0) return
      await this.fetchList()
    },

    async fetchList() {
      this.loading = true
      this.error = null
      try {
        const { axiosRequest } = useRequest()
        const query = buildQuery(this)
        const res = await axiosRequest('GET', `/api/v1/admin/global_components?${query}`, {}, false)
        if (!res.js_error && res.response.success) {
          this.records = res.response.data.records
          this.page = { ...this.page, ...res.response.data.page }
          this.counts = res.response.data.counts
          this.fetchedAt = Date.now()
        } else {
          this.error = 'Failed to load catalog entries.'
        }
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
      }
    },

    // Admin-authored entries are created pre-approved (see the controller's
    // create action) — no review queue for the admin's own submissions.
    // Doesn't touch `records`/`counts` directly: the caller (the Add slide-over)
    // keeps its own local copy of the response, and the list re-fetches next
    // time it's shown stale (see invalidate below).
    async create(payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('POST', '/api/v1/admin/global_components', { global_component: payload }, false)
      if (!res.js_error && res.response.success) this.invalidate()
      return res
    },

    async approve(id) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `/api/v1/admin/global_components/${id}/approve`, {}, false)
      if (!res.js_error && res.response.success) this.applyRecord(res.response.data)
      return res
    },

    async reject(id) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `/api/v1/admin/global_components/${id}/reject`, {}, false)
      if (!res.js_error && res.response.success) this.applyRecord(res.response.data)
      return res
    },

    // Reverts back to pending — for undoing an approve/reject/mark_org_specific made in error.
    async unapprove(id) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `/api/v1/admin/global_components/${id}/unapprove`, {}, false)
      if (!res.js_error && res.response.success) this.applyRecord(res.response.data)
      return res
    },

    // Application-context only (server enforces this) — declares this entry
    // belongs to just one org, never part of the shared public catalog.
    async markOrgSpecific(id) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `/api/v1/admin/global_components/${id}/mark_org_specific`, {}, false)
      if (!res.js_error && res.response.success) this.applyRecord(res.response.data)
      return res
    },

    async update(id, payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `/api/v1/admin/global_components/${id}`, { global_component: payload }, false)
      if (!res.js_error && res.response.success) this.applyRecord(res.response.data)
      return res
    },

    // Patches a record in place and keeps the pending/approved/rejected
    // counts in sync, so the UI reflects a status change without a refetch.
    applyRecord(updated) {
      const index = this.records.findIndex((r) => r.id === updated.id)
      if (index === -1) return

      const previousStatus = this.records[index].status
      this.records[index] = { ...this.records[index], ...updated }
      if (previousStatus !== updated.status) {
        this.counts[previousStatus] -= 1
        this.counts[updated.status] += 1
      }
    },

    invalidate() {
      this.fetchedAt = null
    },

    setSearch(val) {
      this.search = val
      this.page.number = 1
      this.invalidate()
    },

    setStatus(val) {
      this.status = val
      this.page.number = 1
      this.invalidate()
    },

    setType(val) {
      this.type = val
      this.page.number = 1
      this.invalidate()
    },

    setHasPendingVersions(val) {
      this.hasPendingVersions = val
      this.page.number = 1
      this.invalidate()
    },

    setContextTypes(vals) {
      this.contextTypes = vals
      this.page.number = 1
      this.invalidate()
    },

    setSort(by, order) {
      this.sortBy = by
      this.sortOrder = order
      this.page.number = 1
      this.invalidate()
    },

    goToPage(number) {
      this.page.number = number
      this.invalidate()
    },
  },
})
